<template>
  <div
    :class="editPageLoading && 'bg-white'"
    class="relative flex flex-col min-w-0 break-words w-full shadow-lg rounded-lg bg-blueGray-100 border-0"
  >
    {{ componentList }}
    <BannerNotification />
    <BlogEditPageHeader
      :instanceId="this.$route.params.instanceId"
      :instanceName="
        blogInstance?.instance_name
          ? blogInstance?.instance_name.split('|')[1]
          : ''
      "
      :formData="formData"
      :validationStatus="validationStatus"
      :handleEdit="handleEdit"
      :loading="editPageLoading"
    />
    <BlogForm
      pageStatus="edit"
      v-if="!editPageLoading"
      :formData="formData"
      :validationStatus="validationStatus"
      @update:validationStatus="handleUpdateValidationStatus"
    />
    <div
      v-else
      class="bg-white w-full flex flex-col py-10 justify-center items-center"
    >
      <!-- https://gannon.tv/intro -->
      <img width="260" src="@/assets/imgs/loading.gif" alt="loading" />
      <h3>Loading...</h3>
    </div>
  </div>
</template>

<script>
import * as Actions from "@/store/actions.type";
import BannerNotification from "@/components/Shared/Notification/BannerNotification";
import BlogForm from "@/components/ComponentModule/BlogComponent/BlogForm";
import BlogEditPageHeader from "@/components/ComponentModule/BlogComponent/BlogEditPageHeader";
import { mapGetters } from "vuex";
export default {
  name: "BlogEditPage",
  components: {
    BlogEditPageHeader,
    BannerNotification,
    BlogForm
  },
  data() {
    return {
      formData: {
        blog: {
          abi_blog_title: "",
          abi_blog_components: "",
          abi_blog_post_id: ""
        },
        components: []
      },
      editPageLoading: true,
      validationStatus: true
    };
  },
  computed: {
    shortCode() {
      const componentList = this.formData.blog.abi_blog_components.split(",");
      let shortcode = "";
      componentList.forEach((item) => {
        const id = item.split("|")[0];
        const componentName = this.getComponentName(id);
        const name = item.split("|")[1];
        shortcode += `[${componentName
          .toLowerCase()
          .replaceAll(" ", "_")} name=${id}|${name}]`;
      });
      return shortcode;
    },
    blogComponentsList() {
      if (this.blogInstance?.attributes?.abi_blog_components) {
        return this.blogInstance.attributes.abi_blog_components.split(",");
      } else {
        return [];
      }
    },
    blogInstance() {
      if (this.getInstanceInfo && this.getInstanceInfo.length) {
        const foundItem = this.getInstanceInfo.find(
          (item) => item.id === this.$route.params.instanceId
        );
        return foundItem || {};
      } else {
        return {};
      }
    },
    ...mapGetters([
      "getAllComponent",
      "getInstanceInfo",
      "getBlogComponentInstances",
      "getBlogSelectedInstance",
      "getRegion",
      "currentUser"
    ])
  },
  mounted() {
    if (!this.getAllComponent.length) {
      this.$store.dispatch(Actions.FETCH_ALL_COMPONENT, {
        region: this.getRegion,
        hardRefresh: false
      });
    } else {
      this.$store
        .dispatch(Actions.FETCH_BLOG_COMPONENT_INSTANCE, {
          region: this.getRegion,
          componentId: this.getComponentId("abi_blog")
        })
        .then(() => {
          if (
            this.blogInstance &&
            this.blogInstance.id &&
            this.blogInstance.attributes
          ) {
            this.formData.blogInstanceId = this.blogInstance.id;
            this.formData.blog.abi_blog_title =
              this.blogInstance.attributes["abi_blog_title"] || "";
            this.formData.blog.abi_blog_post_id =
              this.blogInstance.attributes["abi_blog_post_id"] || "";
            this.loadFormData();
          }
        });
    }
  },
  watch: {
    getAllComponent() {
      if (this.getAllComponent.length) {
        this.$store.dispatch(Actions.FETCH_BLOG_COMPONENT_INSTANCE, {
          region: this.getRegion,
          componentId: this.getComponentId("abi_blog")
        });
      }
    }
  },
  methods: {
    getComponentId(componentName) {
      const foundItem = this.getAllComponent.find(
        (item) => item.name === componentName
      );
      if (foundItem) {
        return foundItem.id;
      } else {
        return null;
      }
    },
    getComponentName(componentId) {
      const foundItem = this.getAllComponent.find(
        (item) => item.id === componentId
      );
      if (foundItem) {
        return foundItem.name;
      } else {
        return null;
      }
    },
    handleEdit() {
      this.checkFormDataFormat();
      if (this.validationStatus) {
        this.$store.dispatch(Actions.UPDATE_BLOG_COMPONENT_INSTANCE, {
          region: this.getRegion,
          formData: this.formData,
          shortCode: this.shortCode,
          user: this.currentUser.email
        });
      }
    },
    checkFormDataFormat() {
      const componentList = this.formData.blog.abi_blog_components.split(",");
      // *** VALIDATOR: whether the instance is selected ***
      if (componentList.includes("")) {
        this.validationStatus = false;
        this.$store.dispatch(Actions.SET_BLOG_ERRORS, {
          error: "Please select a component instance"
        });
        return;
      } else {
        componentList.forEach((item) => {
          // *** VALIDATOR: whether the component is selected ***
          if (!item.split("|")[0]) {
            this.validationStatus = false;
            this.$store.dispatch(Actions.SET_BLOG_ERRORS, {
              error: "Please select a section component"
            });
            return;
            // *** VALIDATOR: whether the instance name is entered ***
          } else if (!item.split("|")[1].replace("blog_", "")) {
            this.validationStatus = false;
            this.$store.dispatch(Actions.SET_BLOG_ERRORS, {
              error: "Please enter a section name"
            });
            return;
          } else {
            this.validationStatus = true;
            this.$store.dispatch(Actions.CLEAR_BLOG_ERRORS);
          }
        });
      }
    },
    handleUpdateValidationStatus(val) {
      this.validationStatus = val;
    },
    async loadEditFormData() {
      const componentIdList = [];
      const fetchInstanceRequests = [];
      this.blogComponentsList.forEach((item) => {
        const id = item.split("|")[0];
        // collect all component id
        if (!componentIdList.includes(id)) {
          componentIdList.push(id);
        }
      });
      componentIdList.forEach((id) => {
        // fetch instance first
        fetchInstanceRequests.push(
          this.$store.dispatch(Actions.FETCH_BLOG_COMPONENT_INSTANCE, {
            region: this.getRegion,
            componentId: id
          })
        );
      });
      await Promise.all(fetchInstanceRequests);
      this.blogComponentsList.forEach((item, index) => {
        const compId = item.split("|")[0];
        const instances = this.getBlogComponentInstances[compId];
        if (!instances || !Array.isArray(instances)) {
          console.warn(`No instances found for component ID: ${compId}`);
          return;
        }
        const instance = instances.find((i) => i.instance_name === item);
        if (!instance) {
          console.warn(`Instance not found: ${item}`);
          return;
        }
        this.$store.dispatch(Actions.SET_SELECTED_BLOG_INSTANCE, {
          instanceId: item,
          index
        });
        this.formData.components.push({
          attributes: instance.attributes,
          component: this.getComponentName(compId),
          componentId: compId,
          name: "",
          id: instance.id,
          shouldCreate: "no"
        });
      });
    },
    async loadFormData() {
      this.editPageLoading = true;
      const componentIdList = [];
      const fetchInstanceRequests = [];

      this.blogComponentsList.forEach((item) => {
        const id = item.split("|")[0];
        // collect all component id
        if (!componentIdList.includes(id)) {
          componentIdList.push(id);
        }
      });

      componentIdList.forEach((id) => {
        // fetch instance first
        fetchInstanceRequests.push(
          this.$store.dispatch(Actions.FETCH_BLOG_COMPONENT_INSTANCE, {
            region: this.getRegion,
            componentId: id
          })
        );
      });

      await Promise.all(fetchInstanceRequests);
      this.blogComponentsList.forEach((item, index) => {
        const compId = item.split("|")[0];
        const instances = this.getBlogComponentInstances[compId];
        if (!instances || !Array.isArray(instances)) {
          console.warn(`No instances found for component ID: ${compId}`);
          return;
        }
        const instance = instances.find((i) => i.instance_name === item);
        if (!instance) {
          console.warn(`Instance not found: ${item}`);
          return;
        }
        this.$store.dispatch(Actions.SET_SELECTED_BLOG_INSTANCE, {
          instanceId: item,
          index
        });
        this.formData.components.push({
          attributes: instance.attributes,
          component: this.getComponentName(compId),
          componentId: compId,
          name: "",
          id: instance.id,
          shouldCreate: "no"
        });
      });
      this.editPageLoading = false;
    }
  }
};
</script>
